/**
 * Developer affordances for the Settings modal — currently scoped to
 * reproducing the stale-`main.js` chunk-load failure path locally
 * without needing a fake deploy or network-panel acrobatics.
 *
 * **Production:** every public function in this module short-circuits to
 * a no-op when `process.env.NODE_ENV === 'production'`. Webpack/Next dead-
 * code elimination then drops the bodies, so the production bundle pays
 * neither bytes nor CPU. The `window` augmentation is purely a type-level
 * declaration; nothing is ever assigned in prod.
 *
 * **Why this isn't a generic util.** The chunk-error simulation hook
 * exists specifically to exercise `settingsImportWithRetry` +
 * `SettingsModalScreenErrorBoundary`. Co-locating it next to those two
 * files keeps the blast radius and discoverability small, and avoids
 * setting a precedent for an undifferentiated `window.__devtools` grab
 * bag. If/when other surfaces want their own debug hooks, they should
 * land alongside their own consumers under their own namespaces.
 *
 * **Usage (DevTools console):**
 *
 * ```js
 * // Make every settings-modal lazy import throw a synthetic
 * // ChunkLoadError. Retries kick in (~2 visible `logger.warn` lines)
 * // and `SettingsModalScreenErrorBoundary` renders the inline error UI.
 * window.__settingsModalDevtools.forceChunkError(true);
 *
 * // Open Settings → confirm:
 * //   - the modal stays open with the error UI,
 * //   - the rest of the app behind it is interactive,
 * //   - if a session was recording, it's still recording.
 *
 * // Toggle off:
 * window.__settingsModalDevtools.forceChunkError(false);
 * ```
 *
 * To exercise the recording-specific copy variant of the error UI,
 * start a real local recording before opening Settings — the boundary
 * reads from `useSessionRecordingStore`.
 */

export interface SettingsModalDevtools {
  /**
   * Toggle a global flag that causes every settings-modal lazy import
   * to reject with a synthetic `ChunkLoadError`, exercising the retry
   * loop and the local error boundary. Persists in memory only; cleared
   * by a hard reload.
   *
   * @param enabled - When omitted, defaults to `true`. Pass `false` to
   *   restore normal behaviour without reloading.
   */
  forceChunkError: (enabled?: boolean) => void;
}

declare global {
  interface Window {
    __settingsModalDevtools?: SettingsModalDevtools;
    /**
     * Backing flag read by `isChunkErrorSimulated`. Mutated only via
     * `__settingsModalDevtools.forceChunkError`; treat as private.
     */
    __SETTINGS_MODAL_FORCE_CHUNK_ERROR__?: boolean;
  }
}

const isDev = (): boolean => process.env.NODE_ENV !== 'production';

/**
 * Predicate consumed by `settingsImportWithRetry` to decide whether to
 * substitute the real importer with one that throws a synthetic
 * `ChunkLoadError`. Always returns `false` in production — the
 * dead-code-eliminator removes the rest of the function body there.
 */
export const isChunkErrorSimulated = (): boolean => {
  if (!isDev()) return false;
  if (typeof window === 'undefined') return false;
  return Boolean(window.__SETTINGS_MODAL_FORCE_CHUNK_ERROR__);
};

/**
 * Install the `window.__settingsModalDevtools` surface. Safe to call
 * more than once — second and subsequent calls are no-ops so a hot
 * reload doesn't replace an in-use flag value.
 *
 * Production-mode calls return immediately; nothing is assigned to
 * `window` and the production bundle never grows the API surface.
 */
export const installSettingsModalDevtools = (): void => {
  if (!isDev()) return;
  if (typeof window === 'undefined') return;
  if (window.__settingsModalDevtools) return;

  window.__settingsModalDevtools = {
    forceChunkError(enabled = true) {
      window.__SETTINGS_MODAL_FORCE_CHUNK_ERROR__ = enabled;
      // eslint-disable-next-line no-console
      console.info(
        `[settings-modal devtools] forceChunkError = ${enabled}. ` +
          `Open Settings to see the ${enabled ? 'error path' : 'happy path'}.`
      );
    },
  };
};
